import type React from "react";

import { useState } from "react";
import { useTranslation } from "react-i18next";
import { Card, CardContent } from "~/atoms/card";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "~/atoms/tooltip";
import { Textarea } from "~/atoms/textarea";
import { StepHeader } from "~/molecules/WorkflowBuilderStepHeader";
import { StepUsers } from "~/molecules/WorkflowBuilderStepUsers";
import { AlertTriangle } from "lucide-react";
import {
  type WorkflowStepInput,
  type WorkflowStepUser,
  type WorkflowStepType,
  type WorkflowStepUserPermission,
  WorkflowStepStatuses,
} from "~/api/types";
import { cn } from "~/lib/utils";
import { Label } from "~/atoms/label";
import { useSortable } from "@dnd-kit/react/sortable";
import DatePicker from "./DatePicker";
import { ControlledCombobox } from "./ControlledCombobox";

type WorkflowBuilderStepProps = {
  step: WorkflowStepInput;
  index: number;
  onRemove: (stepKey: string) => void;
  onUpdate: (stepKey: string, step: WorkflowStepInput) => void;
  isEditMode: boolean;
  categoryId: number;
  showDueWarning: boolean;
  hideDueDates?: boolean;
};

export function WorkflowBuilderStep({
  step,
  index,
  onRemove,
  onUpdate,
  isEditMode,
  categoryId,
  showDueWarning,
  hideDueDates,
}: WorkflowBuilderStepProps) {
  const { t } = useTranslation();
  const [errors, setErrors] = useState<{ title?: string }>({});

  // A step is editable if it hasn't been reached yet (upcoming status or no id means new)
  const isEditable =
    step.status === WorkflowStepStatuses.upcoming || step.id === undefined;
  const canBeEdited =
    isEditMode && step.status !== WorkflowStepStatuses.completed;
  const canDragStep = isEditMode && isEditable;
  const { ref, isDragging } = useSortable({
    id: step._key,
    index,
    type: "WORKFLOW_STEP",
    accept: "WORKFLOW_STEP",
    disabled: !canDragStep,
  });

  const handleTitleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const newTitle = e.target.value;

    if (!newTitle) {
      setErrors({ title: t(($) => $.commons.workflow.stepTitleRequired) });
    } else {
      setErrors({});
    }

    onUpdate(step._key, {
      ...step,
      name: newTitle,
    });
  };

  const handleDescriptionChange = (
    e: React.ChangeEvent<HTMLTextAreaElement>,
  ) => {
    onUpdate(step._key, {
      ...step,
      description: e.target.value,
    });
  };

  const handleAddUser = (newUser: WorkflowStepUser) => {
    onUpdate(step._key, {
      ...step,
      users: [...step.users, newUser],
    });
  };

  const handleRemoveUser = (userId: number) => {
    onUpdate(step._key, {
      ...step,
      users: step.users.filter((user) => user.userId !== userId),
    });
  };

  const handleEditUserPermission = (
    userId: number,
    permission: WorkflowStepUserPermission,
  ) => {
    onUpdate(step._key, {
      ...step,
      users: step.users.map((user) =>
        user.userId === userId ? { ...user, permission } : user,
      ),
    });
  };

  const handleTypeChange = (type: string) => {
    onUpdate(step._key, {
      ...step,
      type: type as WorkflowStepType,
    });
  };

  const handleDueDateChange = (dueDate?: Date) => {
    onUpdate(step._key, {
      ...step,
      dueAt: dueDate ?? null,
    });
  };

  return (
    <Card
      ref={ref}
      className={cn(
        "w-full overflow-hidden border transition-all duration-200",
        {
          "border-blue bg-blue-50/70":
            step.status === WorkflowStepStatuses.active,
          "border-green bg-green-50/70":
            step.status === WorkflowStepStatuses.completed,
          "border-primary/50 bg-primary/5 scale-[0.97] border-2 border-dashed opacity-50":
            isDragging,
          "hover:shadow-md": isEditMode && isEditable,
        },
      )}
    >
      <StepHeader
        title={step.name}
        index={index}
        status={step.status}
        isEditMode={isEditMode}
        isEditable={isEditable}
        onTitleChange={handleTitleChange}
        onRemove={() => onRemove(step._key)}
        error={errors.title}
      />
      <CardContent className="flex flex-col gap-2 pt-4">
        <div
          className={cn(
            "grid gap-3",
            hideDueDates ? "grid-cols-1" : "grid-cols-2",
          )}
        >
          <div>
            <Label className="mb-2">{t(($) => $.commons.workflow.type)}</Label>
            <ControlledCombobox
              value={step.type}
              onChange={handleTypeChange}
              disabled={!canBeEdited}
              options={[
                {
                  value: "review",
                  label: t(($) => $.commons.workflow.review),
                },
                {
                  value: "approval",
                  label:
                    index === 0
                      ? t(
                          ($) =>
                            $.commons.workflow.approvalNotAvailableFirstStep,
                        )
                      : t(($) => $.commons.workflow.approval),
                  disabled: index === 0,
                },
              ]}
            />
          </div>
          {!hideDueDates && (
            <div>
              <div className="mt-1 flex items-center gap-2">
                <Label className="">
                  {t(($) => $.commons.workflow.responseDate)}
                </Label>
                {showDueWarning && (
                  <TooltipProvider>
                    <Tooltip>
                      <TooltipTrigger asChild>
                        <AlertTriangle className="h-4 w-4 text-red-500" />
                      </TooltipTrigger>
                      <TooltipContent>
                        <p>
                          {t(($) => $.commons.workflow.responseDateWarning)}
                        </p>
                      </TooltipContent>
                    </Tooltip>
                  </TooltipProvider>
                )}
              </div>
              <DatePicker
                value={step.dueAt}
                minDate={new Date()}
                disabled={!canBeEdited}
                onChange={handleDueDateChange}
              />
            </div>
          )}
        </div>

        <div>
          <Label>{t(($) => $.commons.workflow.description)}</Label>
          <Textarea
            value={step.description ?? undefined}
            onChange={handleDescriptionChange}
            disabled={!canBeEdited}
            placeholder={t(($) => $.commons.workflow.stepDescription)}
          />
        </div>

        <div>
          <Label>{t(($) => $.commons.workflow.assignedUsers)}</Label>
          <StepUsers
            users={step.users}
            canBeEdited={canBeEdited}
            onRemoveUser={handleRemoveUser}
            onEditUserPermission={handleEditUserPermission}
            onAddUser={handleAddUser}
            categoryId={categoryId}
          />
        </div>
      </CardContent>
    </Card>
  );
}
